import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight, Phone } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { Logo } from "@/components/Logo";
import { nav, site } from "@/config/site";

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => (document.body.style.overflow = "");
  }, [open]);

  const linkCls = ({ isActive }) =>
    `relative py-2 text-sm font-medium tracking-wide transition-colors duration-300 after:absolute after:bottom-0 after:left-0 after:h-px after:bg-gold after:transition-[width] after:duration-300 ${
      isActive ? "text-white after:w-full" : "text-white/70 after:w-0 hover:text-white hover:after:w-full"
    }`;

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 border-b transition-[background-color,border-color,box-shadow] duration-500 ${
        scrolled || open ? "border-line-dark bg-night/90 shadow-[0_10px_40px_-20px_rgba(0,0,0,.6)] backdrop-blur-md" : "border-transparent bg-night/0"
      }`}
      data-testid="site-header"
    >
      <div className="container-x flex h-[76px] items-center justify-between">
        <Logo light />
        <nav aria-label="Navigation principale" className="hidden items-center gap-9 lg:flex">
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.to === "/"} className={linkCls} data-testid={`nav-link-${n.to === "/" ? "accueil" : n.to.slice(1)}`}>
              {n.label}
            </NavLink>
          ))}
          <Link to="/contact#devis" className="btn-gold !py-2.5" data-testid="nav-devis-btn">
            Demander un devis <ArrowUpRight className="h-4 w-4" />
          </Link>
        </nav>
        <button
          type="button"
          className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/20 text-white lg:hidden"
          aria-expanded={open}
          aria-controls="mobile-menu"
          aria-label={open ? "Fermer le menu" : "Ouvrir le menu"}
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            id="mobile-menu"
            className="fixed inset-x-0 top-[76px] bottom-0 z-40 flex flex-col overflow-y-auto bg-night px-6 pb-10 pt-6 lg:hidden"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.3 }}
            data-testid="mobile-menu"
          >
            <nav aria-label="Navigation mobile" className="flex flex-col divide-y divide-white/10">
              {nav.map((n, i) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.to === "/"}
                  className={({ isActive }) => `flex items-center justify-between py-5 font-heading text-2xl font-bold ${isActive ? "text-gold" : "text-white"}`}
                  data-testid={`mobile-nav-link-${n.to === "/" ? "accueil" : n.to.slice(1)}`}
                >
                  {n.label}
                  <span className="text-xs font-medium tracking-[0.2em] text-white/40">0{i + 1}</span>
                </NavLink>
              ))}
            </nav>
            <Link to="/contact#devis" className="btn-gold mt-8 w-full !py-4 text-base" data-testid="mobile-nav-devis-btn">
              Demander un devis <ArrowUpRight className="h-4 w-4" />
            </Link>
            <a href={site.phoneHref} className="mt-6 inline-flex items-center gap-2 text-sm text-white/70">
              <Phone className="h-4 w-4 text-gold" /> {site.phone}
            </a>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
